import {
    Component,
    EventEmitter,
    Output,
    signal,
    WritableSignal,
    Input,
    ElementRef,
    ViewChild,
    TemplateRef,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@app/core/api/auth/auth.service';
import { ModalService } from '@app/shared/components/modal/modal.service';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { finalize, Subject, takeUntil, filter, take } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';

import {
    LucideAngularModule,
    Bell,
    LogOut,
    User,
    Menu,
    CircleUser,
    KeyRound,
    EyeOff,
    TriangleAlert,
    CircleAlert,
} from 'lucide-angular';
import { UserSessionService } from '@app/core/services/user-session.service';
import { UserType } from '@app/enums/UserType.enum';
import { NotificationService } from '@app/core/api/notification/notification.service';
import { EchoService } from '@app/core/services/echo.service';
import { Notification } from '@app/core/types/notification.types';
import { FileUploadComponent, FileUploadItem } from '@app/shared/forms/file-upload/file-upload.component';
import { ButtonComponent } from '@app/shared/forms/button/button.component';
import { PasswordChangeCheckService } from '@app/core/services/password-change-check.service';
import { getFieldError, isFieldInvalid } from '@app/helpers/validators/custom-form-validation';
import { voluntaryDelistValidationMap } from '@app/helpers/validation-maps/voluntary-delist.map';


@Component({
    selector: 'app-navbar',
    standalone: true,
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
    imports: [
        LucideAngularModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        FileUploadComponent,
        ButtonComponent,
    ],
})
export class NavbarComponent implements OnInit, OnDestroy {
    @Input() isMobile = false;
    @Output() sidebarToggle = new EventEmitter<void>();

    readonly Bell = Bell;
    readonly LogOut = LogOut;
    readonly User = User;
    readonly Menu = Menu;
    readonly CircleUser = CircleUser;
    readonly KeyRound = KeyRound;
    readonly EyeOff = EyeOff;
    readonly TriangleAlert = TriangleAlert;
    readonly CircleAlert = CircleAlert;

    authService = inject(AuthService);
    router = inject(Router);
    modalService = inject(ModalService);
    toastService = inject(ToastService);
    userSessionService = inject(UserSessionService);
    notificationService = inject(NotificationService);
    passwordCheckService = inject(PasswordChangeCheckService);
    echoService = inject(EchoService);
    fb = inject(FormBuilder);

    readonly currentUser = this.authService.user();

    notifications = signal<Notification[]>([]);
    unreadCount = signal<number>(0);
    private notificationChannel: any = null;
    private connectionRequestChannel: any = null;

    profilePopoverOpen: WritableSignal<boolean> = signal(false);
    notifPopoverOpen: WritableSignal<boolean> = signal(false);

    destroy$ = new Subject<void>();
    delistForm!: FormGroup;
    supportingDocs: FileUploadItem[] = [];
    isSubmitted = false;
    isSubmitting = false;
    currentModalId: number | null = null;

    checkingActiveRequest = false;

    @ViewChild('profileBtn', { static: false })
    profileBtn!: ElementRef<HTMLButtonElement>;
    @ViewChild('profilePopover', { static: false })
    profilePopover!: ElementRef<HTMLDivElement>;
    @ViewChild('notifBtn', { static: false })
    notifBtn!: ElementRef<HTMLButtonElement>;
    @ViewChild('notifPopover', { static: false })
    notifPopover!: ElementRef<HTMLDivElement>;
    @ViewChild('voluntaryDelistModal') voluntaryDelistModal!: TemplateRef<any>;
    @ViewChild('activeRequestWarningModal') activeRequestWarningModal!: TemplateRef<any>;
    @ViewChild('notEligibleWarningModal') notEligibleWarningModal!: TemplateRef<any>;
    @ViewChild('cancelVoluntaryDelistConfirmModal') cancelVoluntaryDelistConfirmModal!: TemplateRef<any>; 
    @ViewChild('submitVoluntaryDelistConfirmModal') submitVoluntaryDelistConfirmModal!: TemplateRef<any>; 

    ngOnInit() {
        this.initDelistForm();
        this.loadNotifications();
        this.loadUnreadCount();
        this.subscribeToNotifications();
        this.subscribeToConnectionRequest();
    }

    initDelistForm() {
        this.delistForm = this.fb.group({
            reason: ['', [Validators.required, Validators.maxLength(150)]],
        });
    }

    isExternalUser(): boolean {
        const userType = this.currentUser?.user_type;
        return userType === UserType.EXTERNAL;
    }

    private documentClickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (!this.profilePopoverOpen() && !this.notifPopoverOpen()) return;
        if (
            this.profileBtn?.nativeElement.contains(target) ||
            this.profilePopover?.nativeElement.contains(target) ||
            this.notifBtn?.nativeElement.contains(target) ||
            this.notifPopover?.nativeElement.contains(target)
        ) {
            return;
        }
        this.profilePopoverOpen.set(false);
        this.notifPopoverOpen.set(false);
        document.removeEventListener('click', this.documentClickHandler, true);
    };

    toggleProfilePopover() {
        if (this.notifPopoverOpen()) {
            this.notifPopoverOpen.set(false);
        }
        const willOpen = !this.profilePopoverOpen();
        this.profilePopoverOpen.set(willOpen);
        if (willOpen) {
            document.addEventListener('click', this.documentClickHandler, true);
        } else {
            document.removeEventListener('click', this.documentClickHandler, true);
        }
    }

    toggleNotifPopover() {
        if (this.profilePopoverOpen()) {
            this.profilePopoverOpen.set(false);
        }
        const willOpen = !this.notifPopoverOpen();
        this.notifPopoverOpen.set(willOpen);
        if (willOpen) {
            document.addEventListener('click', this.documentClickHandler, true);
        } else {
            document.removeEventListener('click', this.documentClickHandler, true);
        }
    }

    logout() {
        this.authService
            .logout()
            .pipe(
                finalize(() => {
                    this.profilePopoverOpen.set(false);
                    document.removeEventListener('click', this.documentClickHandler, true);

                    this.userSessionService.stopTracking();
                    this.passwordCheckService.reset();
                    this.router.navigate(['/login'], { replaceUrl: true });
                })
            )
            .subscribe({
                next: () => {},
                error: () => {},
            });
    }

    viewProfile() {
        this.profilePopoverOpen.set(false);
        document.removeEventListener('click', this.documentClickHandler, true);
    }

    changePassword() {
        this.profilePopoverOpen.set(false);
        document.removeEventListener('click', this.documentClickHandler, true);
        this.router.navigate(['/change-password']);
    }

    voluntaryDelist() {
        this.profilePopoverOpen.set(false);
        document.removeEventListener('click', this.documentClickHandler, true);

        // check if final approved
        if (!this.isFinallyApproved()) {
            this.showNotFinallyApprovedWarning();
            return;
        }

        console.log('🔍 Checking for active delisting request...');
        this.checkingActiveRequest = true;

        this.authService
            .hasActiveDelistingRequest()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.checkingActiveRequest = false;

                    if (response.has_active_request) {
                        this.showActiveRequestWarning(response.status);
                    } else {
                        this.showDelistForm();
                    }
                },
                error: (error) => {
                    this.checkingActiveRequest = false;
                    this.showDelistForm();
                },
            });
    }

    showActiveRequestWarning(status: string) {
        this.currentModalId = this.modalService.open({
            title: 'Active Delisting Request',
            contentTemplate: this.activeRequestWarningModal,
            contentData: {
                status: status,
            },
            position: 'center',
            animation: 'fade',
            size: 'sm',
        });
    }

    showDelistForm() {
        this.delistForm.reset();
        this.supportingDocs = [];

        this.currentModalId = this.modalService.open({
            title: 'Voluntary Delist Profile',
            contentTemplate: this.voluntaryDelistModal,
            position: 'center',
            animation: 'fade',
            size: 'md',
        });
    }

    onFileSelected(files: FileUploadItem[]) {
        this.supportingDocs = files;
    }

    onFileRemoved(fileId: string) {
        this.supportingDocs = this.supportingDocs.filter((f) => f.id !== fileId);
    }

    onFileValidationError(error: string) {
        this.toastService.show({
            title: 'File Validation Error',
            content: error || 'Invalid file selected',
            type: 'error',
            duration: 5000,
        });
    }

    submitVoluntaryDelist() {
        // Mark all fields as touched to show validation errors
        Object.keys(this.delistForm.controls).forEach((key) => {
            this.delistForm.get(key)?.markAsTouched();
        });

        if (this.delistForm.invalid) {
            const reasonControl = this.delistForm.get('reason');
    
            // check if it's ONLY a maxLength error (character limit exceeded)
            if (reasonControl?.hasError('maxlength') && !reasonControl?.hasError('required')) {
                // don't show toast - field error message is enough
                return;
            }

            this.toastService.show({
                title: 'Validation Error',
                content: 'Please fill in all required fields correctly.',
                duration: 3000,
                position: 'right',
                type: 'error',
            });
            return;
        }

        // Validate files (required: min 1, max 5)
        if (this.supportingDocs.length === 0) {
            this.toastService.show({
                title: 'Validation Error',
                content: 'Please attach at least one supporting document.',
                type: 'error',
                duration: 5000,
                position: 'right',
            });
            return;
        }

        this.isSubmitting = true;

        // Use first file only for backward compatibility with API
        const firstFile = this.supportingDocs[0]?.file;

        this.authService
            .voluntaryDelist({
                reason: this.delistForm.value.reason,
                supporting_docs: firstFile,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (this.currentModalId !== null) {
                        this.modalService.close(this.currentModalId);
                    }
                    this.toastService.show({
                        title: 'Success',
                        content: response.message || 'Voluntary delisting request submitted successfully.',
                        duration: 3000,
                        position: 'right',
                        type: 'success',
                    });
                    this.isSubmitting = false;
                    this.supportingDocs = [];
                },
                error: (error) => {
                    console.error('Error submitting voluntary delist:', error);
                    this.toastService.show({
                        title: 'Error',
                        content: error.error?.message || 'Failed to submit voluntary delisting request.',
                        duration: 3000,
                        position: 'right',
                        type: 'error',
                    });
                    this.isSubmitting = false;
                },
            });
    }

    closeModal() {
        if (this.currentModalId !== null) {
            this.modalService.close(this.currentModalId);
        }
    }

    confirmCancelVoluntaryDelist() {
        const confirmModal = this.modalService.open({
            title: 'Confirm Cancel',
            contentTemplate: this.cancelVoluntaryDelistConfirmModal,
            contentData: {
                onConfirm: () => {
                    this.modalService.close(confirmModal);
                    this.closeModal();
                    this.delistForm.reset();
                    this.supportingDocs = [];
                },
            },
            position: 'center',
            animation: 'fade',
        });
    }

    confirmSubmitVoluntaryDelist() {
        const confirmModal = this.modalService.open({
            title: 'Confirm Submit',
            contentTemplate: this.submitVoluntaryDelistConfirmModal,
            contentData: {
                onConfirm: () => {
                    this.modalService.close(confirmModal);
                    this.submitVoluntaryDelist();
                },
            },
            position: 'center',
            animation: 'fade',
        });
    }

    performSubmitVoluntaryDelist() {
        this.isSubmitting = true;

        const firstFile = this.supportingDocs[0]?.file;

        this.authService
            .voluntaryDelist({
                reason: this.delistForm.value.reason,
                supporting_docs: firstFile,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    if (this.currentModalId !== null) {
                        this.modalService.close(this.currentModalId);
                    }
                    this.toastService.show({
                        title: 'Success',
                        content: response.message || 'Voluntary delisting request submitted successfully.',
                        duration: 3000,
                        position: 'right',
                        type: 'success',
                    });
                    this.isSubmitting = false;
                    this.supportingDocs = [];
                },
                error: (error) => {
                    console.error('Error submitting voluntary delist:', error);
                    this.toastService.show({
                        title: 'Error',
                        content: error.error?.message || 'Failed to submit voluntary delisting request.',
                        duration: 3000,
                        position: 'right',
                        type: 'error',
                    });
                    this.isSubmitting = false;
                },
            });
    }

    isFieldInvalid(fieldName: string): boolean {
        return isFieldInvalid(this.delistForm, fieldName, this.isSubmitted);
    }

    getFieldError(fieldName: string): string | null {
        return getFieldError(this.delistForm, fieldName, voluntaryDelistValidationMap, this.isSubmitted);
    }

    private loadNotifications() {
        this.notificationService
            .getNotifications(1, 10)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    console.log('Loaded notifications:', response);
                    console.log('Notifications array:', response.notifications);
                    this.notifications.set(response.notifications || []);
                },
                error: (error) => {
                    console.error('Error loading notifications:', error);
                    this.notifications.set([]);
                },
            });
    }

    private loadUnreadCount() {
        this.notificationService
            .getUnreadCount()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    console.log('Unread count:', response.unread_count);
                    this.unreadCount.set(response.unread_count);
                },
                error: (error) => {
                    console.error('Error loading unread count:', error);
                },
            });
    }

    private subscribeToNotifications() {
        const userId = this.currentUser?.id;
        if (!userId) {
            console.warn('No user ID in navbar, cannot subscribe to notifications');
            return;
        }

        // Wait for Echo to be connected using observable instead of polling
        if (!this.echoService.isConnected()) {
            // Subscribe to connection ready event (will auto-unsubscribe on destroy)
            this.echoService
                .connectionReady()
                .pipe(
                    filter((isConnected) => isConnected && !this.notificationChannel),
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => {
                    this.setupNotificationChannel(userId);
                });
            return;
        }

        this.setupNotificationChannel(userId);
    }

    private setupNotificationChannel(userId: string) {
        // Subscribe to user's private channel for notifications
        this.notificationChannel = this.echoService.private(`user.${userId}`);

        if (this.notificationChannel) {
            console.log('[Navbar] Notification channel connected for user:', userId);
            console.log('[Navbar] Listening for .notification() events on channel user.' + userId);

            // Listen for Laravel notification broadcasts
            this.notificationChannel.notification((notification: Notification) => {
                console.log('[Navbar] Received notification via .notification():', notification);

                // Add notification to the list
                const currentNotifs = this.notifications();
                this.notifications.set([notification, ...currentNotifs]);

                // Increment unread count
                this.unreadCount.update((count) => count + 1);
            });

            console.log('Navbar subscribed to notification channel:', `user.${userId}`);
        }
    }

    private subscribeToConnectionRequest() {
        const userId = this.currentUser?.id;
        if (!userId) {
            console.warn('No user ID in navbar, cannot subscribe to connection request');
            return;
        }

        if (!this.echoService.isConnected()) {
            // Subscribe to connection ready event (will auto-unsubscribe on destroy)
            this.echoService
                .connectionReady()
                .pipe(
                    filter((isConnected) => isConnected && !this.connectionRequestChannel),
                    take(1),
                    takeUntil(this.destroy$)
                )
                .subscribe(() => {
                    this.setupConnectionRequestChannel(userId);
                });
            return;
        }

        this.setupConnectionRequestChannel(userId);
    }

    private setupConnectionRequestChannel(userId: string) {
        this.connectionRequestChannel = this.echoService.private(`user.${userId}`);

        if (this.connectionRequestChannel) {
            console.log('[Navbar] Connection request channel connected for user:', userId);
            this.connectionRequestChannel.listen('.connection.request.received', (event: any) => {
                console.log('REQUEST RECEIVED:', event);

                const currentNotifs = this.notifications();
                this.notifications.set([event, ...currentNotifs]);

                this.unreadCount.update((count) => count + 1);
            });

            console.log('Navbar subscribed to connection request channel:', `user.${userId}`);
        }
    }

    private unsubscribeFromNotifications() {
        if (this.notificationChannel) {
            const userId = this.currentUser?.id;
            if (userId) {
                this.echoService.leaveChannel(`user.${userId}`);
            }
            this.notificationChannel = null;
        }
    }

    private unsubscribeFromConnectionRequest() {
        if (this.connectionRequestChannel) {
            const userId = this.currentUser?.id;
            if (userId) {
                this.echoService.leaveChannel(`user.${userId}`);
            }
            this.connectionRequestChannel = null;
        }
    }

    onNotificationClick(notification: Notification) {
        // Mark as read if unread
        if (!notification.read_at) {
            this.notificationService
                .markAsRead(notification.id)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: () => {
                        // Update local notification state
                        const updatedNotifications = this.notifications().map((n) =>
                            n.id === notification.id ? { ...n, read_at: new Date().toISOString() } : n
                        );
                        this.notifications.set(updatedNotifications);

                        // Decrement unread count
                        this.unreadCount.update((count) => Math.max(0, count - 1));
                    },
                    error: (error) => {
                        console.error('Error marking notification as read:', error);
                    },
                });
        }

        if (notification?.category === 'ConnectionRequest' || notification.data?.category === 'ConnectionRequest') {
            this.notifPopoverOpen.set(false);
            this.router.navigate(['/connection-requests']);
        }

        // Navigate to chat if redirectToChat is true
        if (notification.data.redirectToChat && notification.data.chatId) {
            this.notifPopoverOpen.set(false);
            document.removeEventListener('click', this.documentClickHandler, true);
            this.router.navigate(['/chat'], {
                queryParams: { chatId: notification.data.chatId },
            });
        }
    }

    markAllNotificationsAsRead() {
        this.notificationService
            .markAllAsRead()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    // Update all notifications to read
                    const updatedNotifications = this.notifications().map((n) => ({
                        ...n,
                        read_at: n.read_at || new Date().toISOString(),
                    }));
                    this.notifications.set(updatedNotifications);
                    this.unreadCount.set(0);

                    this.notifPopoverOpen.set(false);
                },
                error: (error) => {
                    console.error('Error marking all as read:', error);
                    this.toastService.show({
                        title: 'Error',
                        content: 'Failed to mark notifications as read',
                        duration: 3000,
                        position: 'right',
                        type: 'error',
                    });
                },
            });
    }

    ngOnDestroy() {
        this.unsubscribeFromNotifications();
        this.unsubscribeFromConnectionRequest();
        document.removeEventListener('click', this.documentClickHandler, true);
        this.destroy$.next();
        this.destroy$.complete();
    }

    isFinallyApproved(): boolean {
        const user = this.currentUser as any;
        
        if (!user?.registrationApplication?.status) {
            return false;
        }
        
        const isFinallyApproved = user.registrationApplication.status.action_key === 'approver_final_approval';
        
        return isFinallyApproved;
    }

    showNotFinallyApprovedWarning() {
        this.currentModalId = this.modalService.open({
            title: 'Not Eligible',
            contentTemplate: this.notEligibleWarningModal,  
            position: 'center',
            animation: 'fade',
            size: 'sm',
        });
    }
}
